import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { CheckCircle2, AlertCircle, Plus, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const CATS = ["Plumbing","Electrical","Roofing","Landscaping","Renovation","HVAC","Painting","Flooring","Cleaners","Photography","Private Home Chef","Child Care"];

function ContractorDash({ user, refresh }) {
  const [profile, setProfile] = useState({
    business_name: "", category: "Plumbing", bio: "", phone: "", location: "Columbia Valley",
    hourly_rate: "", years_experience: 0, portfolio_images: [], cover_image: "", liability_accepted: false
  });
  const [imgInput, setImgInput] = useState("");
  const [bids, setBids] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/contractors/me/profile");
      if (data?.user_id) setProfile({ ...profile, ...data });
      const r = await api.get("/bids/mine");
      setBids(r.data);
    })();
    /* eslint-disable-next-line */
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!profile.liability_accepted) {
      toast.error("You must accept the liability terms to publish your profile");
      return;
    }
    try {
      const payload = { ...profile, hourly_rate: profile.hourly_rate ? parseFloat(profile.hourly_rate) : null, years_experience: parseInt(profile.years_experience) || 0 };
      await api.post("/contractors/profile", payload);
      toast.success("Profile saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Tabs defaultValue="profile" className="w-full">
      <TabsList>
        <TabsTrigger data-testid="tab-profile" value="profile">My Profile</TabsTrigger>
        <TabsTrigger data-testid="tab-bids" value="bids">My Bids</TabsTrigger>
        <TabsTrigger data-testid="tab-subscription" value="subscription">Subscription</TabsTrigger>
      </TabsList>

      <TabsContent value="profile">
        <form onSubmit={save} className="bg-white border border-[#E2DCD0] rounded-xl p-6 space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Business name</Label>
              <Input data-testid="profile-business-input" required value={profile.business_name} onChange={e => setProfile({...profile, business_name: e.target.value})} />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={profile.category} onValueChange={v => setProfile({...profile, category: v})}>
                <SelectTrigger data-testid="profile-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Bio</Label>
            <Textarea data-testid="profile-bio-input" rows={4} value={profile.bio} onChange={e => setProfile({...profile, bio: e.target.value})} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label>Location</Label><Input value={profile.location} onChange={e => setProfile({...profile, location: e.target.value})} /></div>
            <div><Label>Phone</Label><Input value={profile.phone} onChange={e => setProfile({...profile, phone: e.target.value})} /></div>
            <div><Label>Hourly rate ($)</Label><Input type="number" value={profile.hourly_rate} onChange={e => setProfile({...profile, hourly_rate: e.target.value})} /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Years experience</Label><Input type="number" value={profile.years_experience} onChange={e => setProfile({...profile, years_experience: e.target.value})} /></div>
            <div><Label>Cover image URL</Label><Input value={profile.cover_image} onChange={e => setProfile({...profile, cover_image: e.target.value})} placeholder="https://…" /></div>
          </div>
          <div>
            <Label>Portfolio images (URLs)</Label>
            <div className="flex gap-2">
              <Input placeholder="https://image.url" value={imgInput} onChange={e => setImgInput(e.target.value)} />
              <Button type="button" onClick={() => { if (imgInput) { setProfile({...profile, portfolio_images: [...profile.portfolio_images, imgInput]}); setImgInput(""); }}} variant="outline">Add</Button>
            </div>
            {profile.portfolio_images.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {profile.portfolio_images.map((u, i) => (
                  <div key={i} className="relative">
                    <img src={u} className="w-24 h-24 object-cover rounded-lg" alt="" />
                    <button type="button" onClick={() => setProfile({...profile, portfolio_images: profile.portfolio_images.filter((_, j) => j !== i)})} className="absolute -top-1 -right-1 bg-stone-800 text-white text-xs w-5 h-5 rounded-full">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="p-4 bg-[#FBF6EE] border-2 border-[#B85C47]/30 rounded-lg">
            <div className="overline text-[#B85C47] mb-2">Contractor Liability Agreement</div>
            <p className="text-xs text-stone-700 leading-relaxed mb-3">
              By publishing your profile you re-confirm you assume all liability for work performed, indemnify Kootenay Home Pros, and carry appropriate insurance & licenses for British Columbia. <Link to="/terms" className="text-[#B85C47] font-semibold underline">Read full terms</Link>.
            </p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                data-testid="profile-liability-checkbox"
                type="checkbox"
                checked={profile.liability_accepted}
                onChange={e => setProfile({...profile, liability_accepted: e.target.checked})}
                className="mt-0.5 w-4 h-4 accent-[#314A3E]"
              />
              <span className="text-xs text-stone-700">I accept the Contractor Liability &amp; Indemnification Agreement.</span>
            </label>
          </div>
          <Button data-testid="save-profile-btn" type="submit" className="bg-[#314A3E] hover:bg-[#243a30] text-white">Save profile</Button>
        </form>
      </TabsContent>

      <TabsContent value="bids" className="mt-4">
        {bids.length === 0 ? <div className="text-stone-500 text-sm">No bids submitted yet.</div> :
          <div className="space-y-3">{bids.map(b => (
            <div key={b.bid_id} className="bg-white border border-[#E2DCD0] rounded-xl p-5 flex justify-between items-start">
              <div>
                <div className="font-semibold tracking-tight">{b.project?.title || "Project"}</div>
                <div className="text-sm text-stone-600 mt-1 line-clamp-2">{b.message}</div>
              </div>
              <div className="text-[#B85C47] font-bold">${b.amount}</div>
            </div>
          ))}</div>
        }
      </TabsContent>

      <TabsContent value="subscription" className="mt-4">
        <SubscriptionPanel user={user} />
      </TabsContent>
    </Tabs>
  );
}

function HomeownerDash({ user }) {
  const [projects, setProjects] = useState([]);
  useEffect(() => { (async () => { const { data } = await api.get("/projects/mine/list"); setProjects(data); })(); }, []);
  return (
    <Tabs defaultValue="projects" className="w-full">
      <TabsList>
        <TabsTrigger data-testid="tab-my-projects" value="projects">My Projects</TabsTrigger>
        <TabsTrigger data-testid="tab-homeowner-sub" value="subscription">Subscription</TabsTrigger>
      </TabsList>
      <TabsContent value="projects" className="mt-4">
        <div className="flex justify-end mb-4">
          <Link to="/projects/new"><Button data-testid="new-project-btn" className="bg-[#B85C47] hover:bg-[#a04e3b] text-white"><Plus className="w-4 h-4 mr-2" /> New project</Button></Link>
        </div>
        {projects.length === 0 ? (
          <div className="text-center py-10 bg-white border border-[#E2DCD0] rounded-xl text-stone-500">
            No projects yet. <Link to="/projects/new" className="text-[#B85C47] font-semibold">Post your first one</Link>.
          </div>
        ) : (
          <div className="space-y-3">{projects.map(p => (
            <Link key={p.project_id} to={`/projects/${p.project_id}`} className="block bg-white border border-[#E2DCD0] rounded-xl p-5 hover:shadow-md transition-all">
              <div className="flex justify-between items-start">
                <div>
                  <Badge className="bg-[#EAE5D9] text-[#314A3E] hover:bg-[#EAE5D9]">{p.category}</Badge>
                  <h3 className="font-semibold mt-2 tracking-tight" style={{ fontFamily: "Outfit" }}>{p.title}</h3>
                </div>
                <div className="text-sm text-stone-500">{p.bid_count} bid{p.bid_count !== 1 && "s"} <ArrowRight className="inline w-3 h-3" /></div>
              </div>
            </Link>
          ))}</div>
        )}
      </TabsContent>
      <TabsContent value="subscription" className="mt-4">
        <SubscriptionPanel user={user} />
      </TabsContent>
    </Tabs>
  );
}

function BoostCard() {
  const [loading, setLoading] = useState(false);
  const boost = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "featured_listing",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Boost failed");
      setLoading(false);
    }
  };
  return (
    <div className="mt-4 bg-gradient-to-br from-[#314A3E] to-[#243a30] text-white rounded-xl p-6">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-5 h-5 text-[#EAB89F]" />
        <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Boost your listing</h3>
      </div>
      <p className="text-sm text-white/85 mb-4 leading-relaxed">
        Get featured on the homepage and pinned to the top of the directory for 7 days. Just <span className="font-bold text-[#EAB89F]">$20</span>.
      </p>
      <Button data-testid="boost-listing-btn" onClick={boost} disabled={loading} className="bg-[#B85C47] hover:bg-[#a04e3b] text-white">
        {loading ? "Redirecting…" : "Get Featured for $20"}
      </Button>
    </div>
  );
}

function SubscriptionPanel({ user }) {
  return (
    <div className="bg-white border border-[#E2DCD0] rounded-xl p-6">
      {user.subscription_active ? (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-[#314A3E]" />
            <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Active subscription</h3>
          </div>
          <div className="text-sm text-stone-600 mb-2">Plan: <span className="font-semibold">{user.subscription_plan}</span></div>
          <div className="text-sm text-stone-600">Renews/expires: {user.subscription_expires_at ? new Date(user.subscription_expires_at).toLocaleDateString() : "—"}</div>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-[#B85C47]" />
            <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>No active plan</h3>
          </div>
          <p className="text-sm text-stone-600 mb-4">Subscribe to unlock posting projects and bidding.</p>
          <Link to="/pricing"><Button data-testid="view-pricing-btn" className="bg-[#B85C47] hover:bg-[#a04e3b] text-white">View plans</Button></Link>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [user, loading, navigate]);
  if (loading || !user) return <div className="max-w-4xl mx-auto px-5 py-20 text-stone-500">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-10 py-12">
      <div className="mb-8">
        <div className="overline text-[#B85C47] mb-2">Dashboard</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Hi {user.name.split(" ")[0]}</h1>
        <div className="text-sm text-stone-500 mt-1 capitalize">{user.role} account</div>
      </div>

      {user.role === "contractor" ? <ContractorDash user={user} refresh={refresh} /> : <HomeownerDash user={user} />}
    </div>
  );
}
